/**
 * FHIR Practitioner supervision relationship helpers.
 *
 * Canonical source of truth: supervisee `supervising-practitioner` extension.
 * Reverse `supervisee-practitioner` extensions on the supervisor are kept in sync
 * for display/editing but are not authoritative on their own.
 */
import type { Practitioner, Reference } from '@medplum/fhirtypes';
import { EXT_URLS } from '@unify/fhir-model/constants';
import {
  getSupervisorRefs,
  getSupervisingPractitionerRef,
} from '@unify/fhir-model/resources/practitioner';
import { toFhirDateOnlyLocal } from '../dateHelpers';

export function normalizePractitionerReference(
  ref: Reference | undefined,
): Reference<Practitioner> | undefined {
  if (!ref?.reference) return undefined;
  const match = ref.reference.match(/Practitioner\/([^/]+)$/);
  if (!match) return undefined;
  const normalized: Reference<Practitioner> = {
    reference: `Practitioner/${match[1]}`,
    ...(ref.display ? { display: ref.display } : {}),
  };
  return normalized;
}

export function practitionerReferenceId(ref: Reference | undefined): string | undefined {
  return normalizePractitionerReference(ref)?.reference?.replace(/^Practitioner\//, '');
}

export function getSupervisorReference(
  practitioner: Practitioner,
): Reference<Practitioner> | undefined {
  return normalizePractitionerReference(getSupervisingPractitionerRef(practitioner));
}

/** All supervisor edges (canonical; usually 0..1). */
export function getSupervisorReferences(
  practitioner: Practitioner,
): Reference<Practitioner>[] {
  return getSupervisorRefs(practitioner)
    .map((r) => normalizePractitionerReference(r))
    .filter((r): r is Reference<Practitioner> => !!r);
}

export function getSupervisionStartedAt(practitioner: Practitioner): string | undefined {
  return practitioner.extension?.find((e) => e.url === EXT_URLS.supervisionStartedAt)?.valueDate;
}

export function setSupervisorReference(
  practitioner: Practitioner,
  supervisor: Reference<Practitioner> | undefined,
  options?: { startedAt?: string },
): Practitioner {
  const without = (practitioner.extension ?? []).filter(
    (e) => e.url !== EXT_URLS.supervisingPractitioner && e.url !== EXT_URLS.supervisionStartedAt,
  );
  if (!supervisor) {
    return without.length > 0 ? { ...practitioner, extension: without } : { ...practitioner, extension: undefined };
  }
  const normalized = normalizePractitionerReference(supervisor);
  if (!normalized) return practitioner;
  const startedAt = options?.startedAt ?? toFhirDateOnlyLocal(new Date());
  return {
    ...practitioner,
    extension: [
      ...without,
      { url: EXT_URLS.supervisingPractitioner, valueReference: normalized },
      { url: EXT_URLS.supervisionStartedAt, valueDate: startedAt },
    ],
  };
}

export function getSuperviseeReferences(practitioner: Practitioner): Reference<Practitioner>[] {
  const refs: Reference<Practitioner>[] = [];
  const seen = new Set<string>();
  for (const ext of practitioner.extension ?? []) {
    if (ext.url !== EXT_URLS.superviseePractitioner) continue;
    const normalized = normalizePractitionerReference(ext.valueReference);
    const id = practitionerReferenceId(normalized);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    refs.push(normalized!);
  }
  return refs;
}

export function setSuperviseeReferences(
  practitioner: Practitioner,
  supervisees: Reference<Practitioner>[],
): Practitioner {
  const without = (practitioner.extension ?? []).filter(
    (e) => e.url !== EXT_URLS.superviseePractitioner,
  );
  const seen = new Set<string>();
  const additions = supervisees
    .map((ref) => normalizePractitionerReference(ref))
    .filter((ref): ref is Reference<Practitioner> => {
      const id = practitionerReferenceId(ref);
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .map((ref) => ({
      url: EXT_URLS.superviseePractitioner,
      valueReference: ref,
    }));
  const extension = [...without, ...additions];
  return { ...practitioner, extension: extension.length > 0 ? extension : undefined };
}

export function addSuperviseeReference(
  supervisor: Practitioner,
  supervisee: Reference<Practitioner>,
): Practitioner {
  const normalized = normalizePractitionerReference(supervisee);
  if (!normalized) return supervisor;
  const existing = getSuperviseeReferences(supervisor);
  const superviseeId = practitionerReferenceId(normalized);
  if (existing.some((r) => practitionerReferenceId(r) === superviseeId)) {
    return supervisor;
  }
  return setSuperviseeReferences(supervisor, [...existing, normalized]);
}

export function removeSuperviseeReference(
  supervisor: Practitioner,
  supervisee: Reference<Practitioner>,
): Practitioner {
  const removeId = practitionerReferenceId(supervisee);
  if (!removeId) return supervisor;
  const remaining = getSuperviseeReferences(supervisor).filter(
    (r) => practitionerReferenceId(r) !== removeId,
  );
  return setSuperviseeReferences(supervisor, remaining);
}

export function dedupeSupervisionExtensions(practitioner: Practitioner): Practitioner {
  let updated = practitioner;
  const supervisor = getSupervisorReference(updated);
  updated = setSupervisorReference(updated, supervisor);
  const supervisees = getSuperviseeReferences(updated);
  updated = setSuperviseeReferences(updated, supervisees);
  return updated;
}

/** True when provider-side canonical supervisor ref differs from reverse supervisee list on supervisor. */
export function supervisingExtensionMatchesSupervisor(
  extension: { url?: string; valueReference?: { reference?: string } } | undefined,
  supervisorPractitionerId: string,
): boolean {
  if (!extension || extension.url !== EXT_URLS.supervisingPractitioner) return false;
  const refId = practitionerReferenceId(extension.valueReference);
  return refId === supervisorPractitionerId;
}

/** FHIR `active` defaults to true when omitted. */
export function isActivePractitioner(practitioner: Practitioner): boolean {
  return practitioner.active !== false;
}

export function findCanonicalSuperviseeIds(
  allPractitioners: Practitioner[],
  supervisorId: string,
): string[] {
  return allPractitioners
    .filter(
      (p) =>
        isActivePractitioner(p) &&
        getSupervisorRefs(p).some((r) => practitionerReferenceId(r) === supervisorId),
    )
    .map((p) => p.id!)
    .filter(Boolean);
}

/**
 * Active canonical supervisee ids plus reverse-extension drift detection.
 * `superviseeIds` come from provider-side `supervising-practitioner` links only.
 */
export function resolveSuperviseeIds(
  supervisor: Practitioner,
  allPractitioners: Practitioner[],
): { superviseeIds: string[]; inconsistent: boolean; needsReverseSync: boolean } {
  const supervisorId = supervisor.id;
  if (!supervisorId) return { superviseeIds: [], inconsistent: false, needsReverseSync: false };

  const canonicalIds = new Set(findCanonicalSuperviseeIds(allPractitioners, supervisorId));
  const reverseIds = new Set(
    getSuperviseeReferences(supervisor)
      .map((r) => practitionerReferenceId(r))
      .filter((id): id is string => !!id),
  );

  const inconsistent =
    reverseIds.size > 0 &&
    ([...canonicalIds].some((id) => !reverseIds.has(id)) ||
      [...reverseIds].some((id) => !canonicalIds.has(id)));

  const needsReverseSync =
    canonicalIds.size > 0 &&
    (reverseIds.size === 0 || inconsistent);

  return { superviseeIds: [...canonicalIds], inconsistent, needsReverseSync };
}
